import { FormEvent, useEffect, useState } from 'react';
import Select from 'react-select';
import { IListItem } from '@nkr/models/index';
import { HardBlockReason } from '@nkr/enums/hard-block-reason';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Modal } from 'react-bootstrap';
import { MatchOfferDeclineReason } from '@nkr/enums/match-offer-decline-reason';

interface IProps {
    show: boolean,
    alias: string,
    centerAlias: string,
    isGlobalDecline: boolean,
    warnOnHighDeclineRatio: boolean,
    hasMortalityRisk: boolean,
    mortalityRiskPercent: number,
    hardBlockReasons: IListItem[],
    onDeclineDonor: (ids: number[], otherText: string) => void,
    onCancel: () => void
}

const HardBlock = (props: IProps) => {
    const [reason, setReason] = useState(-1);
    const [reasonValid, setReasonValid] = useState(true);
    const [comments, setComments] = useState('');
    const [commentsValid, setCommentsValid] = useState(true);
    const [isDirty, setDirty] = useState(false);
    const [selectedHardBlockReasons, setHardBlockReason] = useState<any[]>([]);
    const otherTypes = [MatchOfferDeclineReason.DonorOther, MatchOfferDeclineReason.SurgeryDateOther, MatchOfferDeclineReason.PatientDeactivateOther];

    const handleCommentsChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setComments(e.currentTarget.value);
    }

    const handleDeclineDonor = () => {
        let otherId = selectedHardBlockReasons.filter(x => otherTypes.includes(x.value));
        let isReasonValid = selectedHardBlockReasons.length > 0;
        let areCommentsValid = selectedHardBlockReasons.length > 0 && otherId.length > 0 ? checkCommentValue() : true;

        setReasonValid(isReasonValid);
        setCommentsValid(areCommentsValid);
        setDirty(true);

        if (isReasonValid && areCommentsValid) {
            props.onDeclineDonor(selectedHardBlockReasons.map(x => x.value), comments);
            setHardBlockReason([]);
            setComments('');
        }
    }

    const checkCommentValue = () => comments ? true : false;

    const handleHardBlockChange = (val) => setHardBlockReason(val)

    const handleCancel = () => {
        setHardBlockReason([]);
        setComments('');
        props.onCancel()
    }

    useEffect(() => {
        if (props.show) {
            setReason(-1);
            setReasonValid(true);
            setComments('');
            setCommentsValid(true);
            setDirty(false);
        }
    }, [props.show]);

    let hardBlockReasons = props.hardBlockReasons.map(x => {
        return { value: x.value, label: x.description }
    });

    let showOtherText = selectedHardBlockReasons != null ? selectedHardBlockReasons.filter(x => otherTypes.includes(x.value)).length > 0 : false;

    let disableDecline = selectedHardBlockReasons == null || selectedHardBlockReasons != null && selectedHardBlockReasons.length == 0;

    return (
        <Modal show={props.show} backdrop='static' onHide={props.onCancel}>
            {props.alias && props.centerAlias ?
                <Modal.Header closeButton>
                    <Modal.Title>Decline Donor {props.alias} from {props.centerAlias}</Modal.Title>
                </Modal.Header>
                :
                <Modal.Header closeButton>
                    <Modal.Title>Decline Donors</Modal.Title>
                </Modal.Header>
            }

            <Modal.Body>
                {isDirty && (!reasonValid || !commentsValid) && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        The items in red below are required to continue...
                    </Alert>
                )}

                {props.warnOnHighDeclineRatio && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        Your patient is on dialysis and continuing to decline a high percentage of potential donors will significantly delay your patient
                        from getting matched and transplanted.
                    </Alert>
                )}

                {props.hasMortalityRisk && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        By declining this potential donor for your patient, it is possible the patient may not receive a match offer for some time.
                        <br />
                        The one year mortality risk for this patient is <strong>{props.mortalityRiskPercent.toFixed(0)}</strong>{`%`}
                    </Alert>
                )}

                {props.isGlobalDecline && (
                    <Alert bsStyle='warning' bsSize='sm' className='text-center'>
                        Please be aware that this donor will be blocked for all recipients
                    </Alert>
                )}

                <FormGroup validationState={isDirty && !reasonValid ? 'error' : null}>
                    <Col xs={12} lg={4} componentClass={ControlLabel}>Reason:</Col>
                    <Col xs={12} lg={8}>
                        <Select
                            options={hardBlockReasons}
                            value={selectedHardBlockReasons}
                            onChange={handleHardBlockChange}
                            isMulti={true}
                        />
                    </Col>
                </FormGroup>

                {showOtherText && (
                    <FormGroup validationState={isDirty && !commentsValid ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Other Reason:</Col>
                        <Col xs={12} lg={8}>
                            <FormControl
                                style={{ resize: 'none' }}
                                value={comments}
                                onChange={handleCommentsChange}
                            />
                        </Col>
                    </FormGroup>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button bsStyle='primary' disabled={disableDecline} onClick={handleDeclineDonor}>Decline Donor</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default HardBlock;